import { useEffect } from "react";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Form, Input, Select, Switch } from "antd";
import { type FormInstance } from "antd/lib/form";
import { useTranslation } from "react-i18next";

import {
  CheckoutVariantTarget,
  isHollywoodLandingTemplate,
  type CheckoutVariant,
  type PageConfiguration,
} from "@fitness-app/data-models/entities/CheckoutVariant";

import { colorThemeOptions } from "../../ProductSettings/utils/theme-options";
import { ProductPageFaqField } from "./ProductPageFaqField";
import { ProductPageSectionListField } from "./ProductPageSectionListField";

interface ProductPageGeneralConfigFormProps {
  formController: FormInstance<Omit<PageConfiguration, "pricing"> & { showPromoVideo: boolean }>;
  model?: Partial<PageConfiguration> | null;
  variant: CheckoutVariant;
}

const ProductPageGeneralConfigForm = ({ formController, model, variant }: ProductPageGeneralConfigFormProps) => {
  const { t } = useTranslation(["products", "common"]);
  const showTimer = Form.useWatch("timer", formController);
  const pricingDisabled = Form.useWatch("pricingDisabled", formController);
  const showPromoVideo = Form.useWatch("showPromoVideo", formController);
  const showGuaranteeBanner = Form.useWatch("guaranteeBanner", formController);
  const landingTemplate = Form.useWatch("landingTemplate", formController);
  const pricingLayout = Form.useWatch("pricingLayout", formController);

  useEffect(() => {
    if (model) {
      formController.setFieldsValue({
        productNameOverride: model.productNameOverride ?? "",
        productImageOverride: model.productImageOverride ?? "",
        productDescriptionOverride: model.productDescriptionOverride ?? "",
        pricingDisabled: model.pricingDisabled ?? false,
        enableLandingPage: model.enableLandingPage ?? false,
        hideHero: model.hideHero ?? false,
        landingTemplate: model.landingTemplate ?? "classic",
        pricingLayout: model.pricingLayout ?? "cards",
        pricingTitle: model.pricingTitle ?? "",
        pricingDescription: model.pricingDescription ?? "",
        pricingBonusesTitle: model.pricingBonusesTitle ?? "",
        pricingHighlight: model.pricingHighlight ?? undefined,
        pageHeading: model.pageHeading ?? undefined,
        campaignHero: model.campaignHero ?? undefined,
        defaultColorScheme: model.defaultColorScheme ?? undefined,
        sectionPlacement: model.sectionPlacement ?? {},
        sectionOrder: model.sectionOrder ?? undefined,
        hiddenSections: model.hiddenSections ?? undefined,
        faq: model.faq ?? undefined,
        richPriceCards: model.richPriceCards ?? false,
        videoOnTop: model.videoOnTop ?? false,
        renewalFallback: model.renewalFallback ?? "",
        timer: model.timer ?? false,
        promoVideo: model.promoVideo ?? undefined,
        showPromoVideo: model.promoVideo?.videoId ? true : false,
        guaranteeBanner: model.guaranteeBanner ?? undefined,
      });
    }
  }, [model]);

  return (
    <Form<Omit<PageConfiguration, "pricing"> & { showPromoVideo: boolean }>
      disabled={pricingDisabled}
      name="page-general-config-form"
      className="w-full max-w-[920px]"
      layout="vertical"
      form={formController}
      initialValues={{
        productNameOverride: model?.productNameOverride ?? "",
        productImageOverride: model?.productImageOverride ?? "",
        productDescriptionOverride: model?.productDescriptionOverride ?? "",
        pricingDisabled: model?.pricingDisabled ?? false,
        landingTemplate: model?.landingTemplate ?? "classic",
        pricingLayout: model?.pricingLayout ?? "cards",
        pricingTitle: model?.pricingTitle ?? "",
        pricingDescription: model?.pricingDescription ?? "",
        pricingBonusesTitle: model?.pricingBonusesTitle ?? "",
        pricingHighlight: model?.pricingHighlight ?? undefined,
        pageHeading: model?.pageHeading ?? undefined,
        campaignHero: model?.campaignHero ?? undefined,
        defaultColorScheme: model?.defaultColorScheme ?? undefined,
        sectionPlacement: model?.sectionPlacement ?? {},
        sectionOrder: model?.sectionOrder ?? undefined,
        hiddenSections: model?.hiddenSections ?? undefined,
        faq: model?.faq ?? undefined,
        richPriceCards: model?.richPriceCards ?? false,
        enableLandingPage: model?.enableLandingPage ?? true,
        hideHero: model?.hideHero ?? false,
        videoOnTop: model?.videoOnTop ?? false,
        renewalFallback: model?.renewalFallback ?? "",
      }}
    >
      <Form.Item
        name="productNameOverride"
        label={t<string>("pageConfig.productNameOverride")}
        tooltip={t<string>("pageConfig.productNameOverrideTooltip")}
      >
        <Input />
      </Form.Item>

      <Form.Item
        name="productImageOverride"
        label={t<string>("pageConfig.productImageOverride")}
        tooltip={t<string>("pageConfig.productImageOverrideTooltip")}
      >
        <Input />
      </Form.Item>

      <Form.Item
        name="productDescriptionOverride"
        label={t<string>("pageConfig.productDescriptionOverride")}
        tooltip={t<string>("pageConfig.productDescriptionOverrideTooltip")}
      >
        <Input.TextArea rows={4} />
      </Form.Item>

      <Form.Item
        name="renewalFallback"
        hidden={variant.target === CheckoutVariantTarget.New}
        label={t<string>("pageConfig.renewalFallback")}
        tooltip={t<string>("pageConfig.renewalFallbackTooltip")}
      >
        <Input />
      </Form.Item>

      <Form.Item
        name="landingTemplate"
        label={t<string>("pageConfig.landingTemplate")}
        tooltip={t<string>("pageConfig.landingTemplateTooltip")}
      >
        <Select
          disabled={false}
          options={[
            { value: "classic", label: t<string>("pageConfig.landingTemplateClassic") },
            { value: "hollywood2026", label: t<string>("pageConfig.landingTemplateHollywood2026") },
            { value: "hollywoodBlackFriday", label: t<string>("pageConfig.landingTemplateHollywoodBlackFriday") },
          ]}
        />
      </Form.Item>

      <Form.Item
        name="defaultColorScheme"
        hidden={landingTemplate === "hollywoodBlackFriday"}
        label="Domyślny motyw strony"
        tooltip="W jakim motywie strona otwiera się klientce. Przełącznik motywu dalej działa, a jej wybór zostaje w przeglądarce i wygrywa."
      >
        <Select
          allowClear
          disabled={false}
          placeholder="Jak w systemie klientki"
          options={[
            { value: "light", label: "Jasny" },
            { value: "dark", label: "Ciemny" },
          ]}
        />
      </Form.Item>

      {isHollywoodLandingTemplate(landingTemplate) && (
        <>
          {landingTemplate === "hollywoodBlackFriday" && (
            <>
              <Form.Item
                name={["campaignHero", "title"]}
                label="Hero: tytuł obok zdjęcia kampanii"
                tooltip="Hero stoi nad licznikiem, a ukrywa je „Ukryj hero”. Puste pole = „Black Hollywood 2026”."
              >
                <Input placeholder="Black Hollywood 2026" />
              </Form.Item>

              <Form.Item name={["campaignHero", "lead"]} label="Hero: zdanie pod tytułem">
                <Input.TextArea autoSize={{ minRows: 2 }} />
              </Form.Item>
            </>
          )}

          <Form.Item
            name={["pageHeading", "title"]}
            label="Nagłówek strony"
            tooltip="Pod licznikiem albo hero. Przedłużenia nie mają hero, więc to zdanie otwiera stronę. Puste pole = bez nagłówka."
          >
            <Input />
          </Form.Item>

          <Form.Item name={["pageHeading", "description"]} label="Podtytuł pod nagłówkiem strony">
            <Input.TextArea autoSize={{ minRows: 2 }} />
          </Form.Item>

          <Form.Item
            name="pricingLayout"
            label="Postać cennika"
            tooltip="Karty pakietów obok siebie albo dwukolumnowa oferta zestawiona wiersz w wiersz - jak na dotychczasowych stronach przedłużeń. W tabeli pozycje łączy ich kolejność, więc ta sama pozycja może mieć w lewej i prawej kolumnie inną nazwę i inną wycenę (albo nie być w jednej z nich wcale). Tabela z opcjami płatności: rata jako główna kwota, pod nią linie „albo …”, a pozycja, której pakiet nie ma, dostaje ✕."
          >
            <Select
              disabled={false}
              options={[
                { value: "cards", label: "Karty pakietów" },
                { value: "comparison", label: "Tabela porównawcza (dwie kolumny)" },
                { value: "optionsTable", label: "Tabela z opcjami płatności (Black Friday)" },
              ]}
            />
          </Form.Item>

          <Form.Item name="pricingTitle" label="Nagłówek cennika" tooltip="Puste pole = domyślny nagłówek szablonu.">
            <Input />
          </Form.Item>

          <Form.Item name="pricingDescription" label="Opis pod nagłówkiem cennika">
            <Input.TextArea autoSize={{ minRows: 2 }} />
          </Form.Item>

          {(pricingLayout === "comparison" || pricingLayout === "optionsTable") && (
            <Form.Item name="pricingBonusesTitle" label="Napis na pasie bonusów" tooltip="Puste pole = „Bonusy”.">
              <Input />
            </Form.Item>
          )}

          <Form.Item
            name={["pricingHighlight", "title"]}
            label="Baner nad cennikiem: tytuł"
            tooltip="Stoi między nagłówkiem cennika a cennikiem. Puste pola = bez banera."
          >
            <Input />
          </Form.Item>

          <Form.Item name={["pricingHighlight", "points"]} label="Baner nad cennikiem: punkty">
            <Select mode="tags" placeholder="Wpisz punkt i naciśnij Enter" tokenSeparators={["\n"]} />
          </Form.Item>

          <Form.Item
            label="Baner nad cennikiem: karty"
            tooltip="Złoty nagłówek karty i zdanie pod nim, np. „430 zł miesięcznie” / „zamiast 497 zł w ratach”. Gdy są karty, baner pokazuje je zamiast punktów."
          >
            <Form.List name={["pricingHighlight", "cards"]}>
              {(fields, { add, remove }) => (
                <div className="flex flex-col gap-2">
                  {fields.map((field) => (
                    <div key={field.key} className="flex items-start gap-2">
                      <Form.Item name={[field.name, "title"]} className="!mb-0 w-[220px]">
                        <Input placeholder="Nagłówek karty" />
                      </Form.Item>
                      <Form.Item name={[field.name, "description"]} className="!mb-0 flex-1">
                        <Input.TextArea autoSize={{ minRows: 1 }} placeholder="Zdanie pod nagłówkiem" />
                      </Form.Item>
                      <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(field.name)} />
                    </div>
                  ))}
                  <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ title: "", description: "" })}>
                    Dodaj kartę
                  </Button>
                </div>
              )}
            </Form.List>
          </Form.Item>

          <ProductPageSectionListField formController={formController} />

          <ProductPageFaqField
            formController={formController}
            landingTemplate={landingTemplate}
            isRenewal={variant.target === CheckoutVariantTarget.Renewal}
          />
        </>
      )}

      <Form.Item
        name="richPriceCards"
        label={t<string>("pageConfig.richPriceCards")}
        tooltip={t<string>("pageConfig.richPriceCardsTooltip")}
        valuePropName="checked"
      >
        <Switch disabled={false} />
      </Form.Item>

      <Form.Item
        name="pricingDisabled"
        label={t<string>("pageConfig.pricingDisabled")}
        tooltip={t<string>("pageConfig.pricingDisabledTooltip")}
        valuePropName="checked"
      >
        <Switch disabled={false} />
      </Form.Item>

      <Form.Item
        name="enableLandingPage"
        hidden={variant.target === CheckoutVariantTarget.New}
        label={t<string>("pageConfig.enableLandingPage")}
        tooltip={t<string>("pageConfig.enableLandingPageTooltip")}
        valuePropName="checked"
      >
        <Switch />
      </Form.Item>

      <Form.Item
        name="hideHero"
        hidden={variant.target === CheckoutVariantTarget.Renewal}
        label="Ukryj hero"
        tooltip="Strona zaczyna się wtedy od wideo albo pierwszej sekcji. Licznika to nie dotyczy — wariant z datą końca i tak otwiera się licznikiem. Przedłużenia nie mają hero z zasady."
        valuePropName="checked"
      >
        <Switch />
      </Form.Item>

      <Form.Item
        name="videoOnTop"
        hidden={variant.target === CheckoutVariantTarget.Renewal}
        label={t<string>("pageConfig.videoOnTop")}
        tooltip={t<string>("pageConfig.videoOnTopTooltip")}
        valuePropName="checked"
      >
        <Switch />
      </Form.Item>

      <Form.Item
        name={["timer", "title"]}
        label={t<string>("pageConfig.timerTitle")}
        tooltip={t<string>("pageConfig.timerTitleTooltip")}
      >
        <Input />
      </Form.Item>

      {showTimer && (
        <>
          <Form.Item name={["timer", "description"]} label={t<string>("pageConfig.timerDescription")}>
            <Input.TextArea rows={3} />
          </Form.Item>

          <Form.Item name={["timer", "startInfo"]} label={t<string>("pageConfig.timerStartInfo")}>
            <Input />
          </Form.Item>

          <Form.Item
            valuePropName="checked"
            name={["timer", "hideCountdown"]}
            label="Ukryj licznik czasu"
            tooltip="Jeśli chcesz ukryć licznik czasu, zaznacz to pole. Sekcja timera pozostanie."
          >
            <Switch />
          </Form.Item>

          <Form.Item
            valuePropName="checked"
            name={["timer", "showSeatsLeft"]}
            label={t<string>("pageConfig.timerShowSeatsLeft")}
          >
            <Switch />
          </Form.Item>

          <Form.Item name={["timer", "titleColor"]} label={t<string>("pageConfig.timerTitleColor")}>
            <Select>
              {colorThemeOptions().map((item) => (
                <Select.Option key={item.value} value={item.value}>
                  {t<string>(`${item.value.toUpperCase()}`)}
                  {item.color && (
                    <div
                      style={{
                        position: "absolute",
                        right: "1.2rem",
                        top: ".35rem",
                        width: "1.2rem",
                        height: "1.2rem",
                        backgroundColor: item.color,
                        borderRadius: ".6rem",
                      }}
                    />
                  )}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </>
      )}

      <Form.Item name="showPromoVideo" label={t<string>("pageConfig.promoVideo")} valuePropName="checked">
        <Switch />
      </Form.Item>

      {showPromoVideo && (
        <>
          <Form.Item name={["promoVideo", "title"]} label={t<string>("pageConfig.promoVideoTitle")}>
            <Input />
          </Form.Item>

          <Form.Item
            name={["promoVideo", "videoId"]}
            label={t<string>("pageConfig.promoVideoId")}
            tooltip={t<string>("pageConfig.promoVideoIdTooltip")}
            rules={[
              {
                required: true,
                message: t<string>("common:validationErrors.fieldIsRequired"),
              },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item name={["promoVideo", "description"]} label={t<string>("pageConfig.promoVideoDescription")}>
            <Input.TextArea rows={2} />
          </Form.Item>

          <Form.Item
            name={["promoVideo", "hideIcons"]}
            label={t<string>("pageConfig.promoVideoHideIcons")}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </>
      )}

      <Form.Item
        name={["guaranteeBanner", "title"]}
        label={t<string>("pageConfig.guaranteeBannerTitle")}
        tooltip={t<string>("pageConfig.guaranteeBannerTitleTooltip")}
      >
        <Input placeholder="np. Gwarancja Kontynuacji Programu Hollywood" />
      </Form.Item>

      {showGuaranteeBanner && (
        <>
          <Form.Item
            name={["guaranteeBanner", "description"]}
            label={t<string>("pageConfig.guaranteeBannerDescription")}
          >
            <Input.TextArea rows={3} placeholder="Opis gwarancji..." />
          </Form.Item>

          <Form.Item
            name={["guaranteeBanner", "benefits"]}
            label={t<string>("pageConfig.guaranteeBannerBenefits")}
            tooltip={t<string>("pageConfig.guaranteeBannerBenefitsTooltip")}
          >
            <Select mode="tags" placeholder="Wpisz benefit i naciśnij Enter" tokenSeparators={["\n"]} />
          </Form.Item>

          <Form.Item
            name={["guaranteeBanner", "limitedAvailability", "text"]}
            label={t<string>("pageConfig.guaranteeBannerLimitedText")}
            tooltip={t<string>("pageConfig.guaranteeBannerLimitedTextTooltip")}
          >
            <Input placeholder="np. Opcja dostępna w ograniczonej liczbie: maksymalnie 4 miejsca miesięcznie" />
          </Form.Item>
        </>
      )}
    </Form>
  );
};

export default ProductPageGeneralConfigForm;
